import { executeRequest } from "@cayuse/shared-react.utils.http";
import {
    isFunction,
    isNonEmptyObject,
    isNonEmptyString,
    isNumber,
} from "@cayuse/shared-react.utils.type-checks";

import {
    ERROR_STATUS_CODE_401,
    ERROR_STATUS_CODE_403,
    ERROR_STATUS_CODE_404,
    IS_DEV,
} from "globals/constants";
import _ from "lodash";
import { formattedCurrency, notifyError } from "utils";

const EMPTY = "$0.00";
const NO_PI = "No PI Specified";

const defaultDictionary = {
    primaryResearcher: "PI",
    fundCode: "Fund Code",
    fundName: "Fund Name",
    obligated: "Obligated",
    obligatedDC: "Direct Cost",
    obligatedFA: "F&A",
    obligatedTotal: "Total",
    expensesToDate: "Expenses to Date",
    expensesToDateDC: "Direct Cost",
    expensesToDateFA: "F&A",
    expensesToDateTotal: "Total",
    budgetHeader: "Budget Remaining",
    budgetRemainingDC: "Direct Cost",
    budgetRemainingFA: "F&A",
    budgetRemainingTotal: "Total",
};

export const mapFunds = ({
    primaryResearcher = "",
    obligatedDc = 0,
    obligatedFa = 0,
    expenseDc = 0,
    expenseFa = 0,
    ...funds
}) => {
    obligatedDc = parseFloat(obligatedDc);
    obligatedFa = parseFloat(obligatedFa);
    expenseDc = parseFloat(expenseDc);
    expenseFa = parseFloat(expenseFa);

    const obligatedTotal = obligatedDc + obligatedFa;
    const expensesToDateTotal = expenseDc + expenseFa;

    const budgetRemainingDC = obligatedDc - expenseDc;
    const budgetRemainingDCPercent = obligatedDc
        ? (budgetRemainingDC / obligatedDc) * 100
        : 0;

    const budgetRemainingFA = obligatedFa - expenseFa;
    const budgetRemainingFAPercent = obligatedFa
        ? (budgetRemainingFA / obligatedFa) * 100
        : 0;
    const budgetRemainingTotal = obligatedTotal - expensesToDateTotal;
    const budgetRemainingTotalPercent = obligatedTotal
        ? (budgetRemainingTotal / obligatedTotal) * 100
        : 0;

    return {
        ...funds,
        primaryResearcher: primaryResearcher ?? "--",
        obligatedDC: formatCurrencyAmount(obligatedDc),
        obligatedFA: formatCurrencyAmount(obligatedFa),
        obligatedTotal: formatCurrencyAmount(obligatedTotal),
        expensesToDateDC: formatCurrencyAmount(expenseDc),
        expensesToDateFA: formatCurrencyAmount(expenseFa),
        expensesToDateTotal: formatCurrencyAmount(expensesToDateTotal),
        budgetRemainingDC: formatCurrencyAmount(budgetRemainingDC),
        budgetRemainingDCPercent: formatPercent(budgetRemainingDCPercent),
        budgetRemainingFA: formatCurrencyAmount(budgetRemainingFA),
        budgetRemainingFAPercent: formatPercent(budgetRemainingFAPercent),
        budgetRemainingTotal: formatCurrencyAmount(budgetRemainingTotal),
        budgetRemainingTotalPercent: formatPercent(budgetRemainingTotalPercent),
    };
};

export const formatCurrencyAmount = (amount) =>
    formattedCurrency(amount) === "--" ? EMPTY : formattedCurrency(amount);

export const formatPercent = (percent) => {
    if (!isNumber(percent)) {
        return "--";
    }

    if (percent === 0) {
        return "--";
    }

    const fixed = percent === 100 ? 0 : 1;
    return `${percent.toFixed(fixed)}%`;
};

export const errorHandler = (error, type) => {
    switch (error.status) {
        case ERROR_STATUS_CODE_401:
            const message =
                type === "search"
                    ? "There was an issue with authorizing this request. Please reach out to your system administrator or Cayuse support for further investigation."
                    : "There was an error with the request. At least one of the linked fund’s information could not be retrieved. Please reach out to your system administrator or Cayuse support for further investigation.";
            notifyError(message, error);
            break;

        case ERROR_STATUS_CODE_403:
            notifyError(
                "There was an issue with authorizing this request. Please reach out to your system administrator or Cayuse support for further investigation.",
                error
            );
            break;

        case ERROR_STATUS_CODE_404:
            notifyError(
                "At least one of the linked fund’s information could not be found. Please reach out to your system administrator or Cayuse support for further investigation.",
                error
            );
            break;
        default:
            notifyError(
                "There was an error with the request. Please reach out to your system administrator or Cayuse support for further investigation.",
                error
            );
    }
};

export const queryFunds = (query, url) => {
    const search = `[["FundCode","contains","${query}"],"or",["FundName","contains","${query}"],"or",["PrimaryResearcher","contains","${query}"]]`;
    const requestUrl = IS_DEV
        ? `/fm-api/api/v1/FundSummary/summaryfilter?skip=0&take=20&requireTotalCount=true&filter=${search}`
        : `${url}/fm-api/api/v1/FundSummary/summaryfilter?skip=0&take=20&requireTotalCount=true&filter=${search}`;

    return executeRequest(requestUrl)
        .then((response) => {
            const options = (response.data ?? []).map((item) => {
                return {
                    key: item.accountId,
                    value: item.accountId,
                    text: item.fundName,
                    fund: item,
                    content: `${item.fundCode} - ${item.fundName} - ${
                        item.primaryResearcher ? item.primaryResearcher : NO_PI
                    }`,
                };
            });

            return {
                options,
                hiddenOptionsCount:
                    response.totalCount > response.data.length &&
                    response.totalCount - response.data.length,
            };
        })
        .catch((error) => {
            errorHandler(error, "search");
            return {
                options: [],
            };
        });
};

export const fetchFunds = (fundIds, url) => {
    let search = "";
    for (const fundId of fundIds) {
        if (isNonEmptyString(search)) {
            search = `${search},"or",`;
        }
        search = `${search}["AccountId","=","${fundId}"]`;
    }

    const requestUrl = IS_DEV
        ? `/fm-api/api/v1/FundSummary/summaryfilter?skip=0&take=1000&requireTotalCount=true&filter=[${search}]`
        : `${url}/fm-api/api/v1/FundSummary/summaryfilter?skip=0&take=1000&requireTotalCount=true&filter=[${search}]`;

    return executeRequest(requestUrl)
        .then((response) => response.data.map((fund) => mapFunds(fund)))
        .catch((error) => {
            errorHandler(error, "fetch");
        });
};

export const fundToCardItems = (fund, dictionary) => {
    const {
        fundView: {
            fundCode = defaultDictionary.fundCode,
            fundName = defaultDictionary.fundName,
            primaryResearcher = defaultDictionary.primaryResearcher,
            expensesToDate = defaultDictionary.expensesToDate,
            obligated = defaultDictionary.obligated,
            budgetHeader = defaultDictionary.budgetHeader,
            obligatedDC = defaultDictionary.obligatedDC,
            obligatedFA = defaultDictionary.obligatedFA,
            obligatedTotal = defaultDictionary.obligatedTotal,
            expensesToDateDC = defaultDictionary.expensesToDateDC,
            expensesToDateFA = defaultDictionary.expensesToDateFA,
            expensesToDateTotal = defaultDictionary.expensesToDateTotal,
            budgetRemainingDC = defaultDictionary.budgetRemainingDC,
            budgetRemainingFA = defaultDictionary.budgetRemainingFA,
            budgetRemainingTotal = defaultDictionary.budgetRemainingTotal,
        },
    } = dictionary;

    const cardItems = [];
    const cardKeys = {
        fundCode,
        fundName,
        primaryResearcher,
        obligated: {
            type: "SECTION_HEADER",
            value: obligated,
        },
        obligatedDC: {
            type: "LEFT_RIGHT_ALIGNED_VALUE",
            label: obligatedDC,
            value: obligatedDC,
        },
        obligatedFA: {
            type: "LEFT_RIGHT_ALIGNED_VALUE",
            label: obligatedFA,
            value: obligatedFA,
        },
        obligatedTotal: {
            type: "LEFT_RIGHT_ALIGNED_VALUE",
            label: obligatedTotal,
            value: obligatedTotal,
        },
        expensesToDate: {
            type: "SECTION_HEADER",
            value: expensesToDate,
        },
        expensesToDateDC: {
            type: "LEFT_RIGHT_ALIGNED_VALUE",
            label: expensesToDateDC,
            value: expensesToDateDC,
        },
        expensesToDateFA: {
            type: "LEFT_RIGHT_ALIGNED_VALUE",
            label: expensesToDateFA,
            value: expensesToDateFA,
        },
        expensesToDateTotal: {
            type: "LEFT_RIGHT_ALIGNED_VALUE",
            label: expensesToDateTotal,
            value: expensesToDateTotal,
        },
        budgetHeader: {
            type: "SECTION_HEADER",
            value: budgetHeader,
        },
        budgetRemainingDC: {
            type: "LEFT_RIGHT_ALIGNED_VALUE",
            label: budgetRemainingDC,
            value: budgetRemainingDC,
        },
        budgetRemainingFA: {
            type: "LEFT_RIGHT_ALIGNED_VALUE",
            label: budgetRemainingFA,
            value: budgetRemainingFA,
        },
        budgetRemainingTotal: {
            type: "LEFT_RIGHT_ALIGNED_VALUE",
            label: budgetRemainingTotal,
            value: budgetRemainingTotal,
        },
    };

    const extractValue = (keyValue, fundValue, label) => {
        if (label) {
            return fundValue;
        }

        return isFunction(keyValue) ? keyValue(fundValue) : keyValue;
    };

    for (const [keyPropName, key] of Object.entries(cardKeys)) {
        const isCustomKey = isNonEmptyObject(key);
        const fundValue = fund[keyPropName];
        let value = isCustomKey
            ? extractValue(key.value, fundValue, key.label)
            : fundValue;

        if (keyPropName.includes("budgetRemaining")) {
            const propName = `${keyPropName}Percent`;
            value = `(${fund[propName]}) ${value}`;
        }

        cardItems.push({ key, value });
    }

    return cardItems;
};
